import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PaymentLinkTable from "../../../components/payment-link-system/PaymentLinkTable";
import {
  selectToken,
  selectUser,
} from "../../../components/auth/auth.selector";
import {
  convertStraight,
  convertThenFormat,
  currentDateOfTheMonth,
} from "../../../components/common-data/Converts";
import { fetchSubsTypeSalesData } from "../../../redux/reducers/apiSlice";
import CmnFilters from "../../../components/lead-management/common/CmnFilters";
import useInterval from "../../../components/common-components/custom-hooks/useInterval";
import "../../../styles/home/sales-values.css";
import TableSkeleton from "../../../components/common-components/TableSkeleton";

const SaleValues = ({ gymId, registerApiCall, isBetaMode, prevAllFilters }) => {
  const token = useSelector(selectToken);
  const userInfoData = useSelector(selectUser);
  const dispatch = useDispatch();
  const { subsTypeSales, subsTypeSalesLoading } = useSelector(
    (state) => state.api
  );

  const isAdmin = userInfoData?.account_type === "admin";
  const isClusterManager = userInfoData?.account_type === "cluster_manager";

  const iniTbDatas = {
    allData: null,
  };
  const [tbDatas, setTbDatas] = useState(iniTbDatas);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  const iniAllFilters = {
    selectedStartDate: currentDateOfTheMonth,
    selectedEndDate: currentDateOfTheMonth,
    selCity: "",
    selAddedby: !isAdmin
      ? {
          label: userInfoData?.name || "",
          value: userInfoData?.uid || userInfoData?.user_id || "",
        }
      : prevAllFilters.selAddedby || "",
  };
  const [allFilters, setAllFilters] = useState(iniAllFilters);
  const initialFilters = {
    gym_id: isClusterManager ? "" : gymId || "",
    date_from: allFilters.selectedStartDate
      ? convertStraight(allFilters.selectedStartDate)
      : "",
    date_to: allFilters.selectedEndDate
      ? convertStraight(allFilters.selectedEndDate)
      : "",
    city: allFilters.selCity?.label || "",
    cluster_manager_id: allFilters.selAddedby?.value || "",
  };
  const [filters, setFilters] = useState(initialFilters);

  const getSortedData = () => {
    if (!tbDatas?.allData || tbDatas.allData.length === 0) return [];

    const totalRow = tbDatas.allData.find(
      (row) => row?.gym_name?.toLowerCase() === "total"
    );
    const nonTotalRows = tbDatas.allData.filter(
      (row) => row?.gym_name?.toLowerCase() !== "total"
    );

    if (sortConfig?.key && sortConfig?.direction) {
      const sortedNonTotalRows = [...nonTotalRows].sort((a, b) => {
        const aValue =
          a && a[sortConfig.key] ? parseFloat(a[sortConfig.key]) : 0;
        const bValue =
          b && b[sortConfig.key] ? parseFloat(b[sortConfig.key]) : 0;

        if (sortConfig.direction === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });

      return totalRow ? [...sortedNonTotalRows, totalRow] : sortedNonTotalRows;
    }

    const sortedNonTotalRows = [...nonTotalRows].sort((a, b) => {
      const aTotal = a && a.total ? parseFloat(a.total) : 0;
      const bTotal = b && b.total ? parseFloat(b.total) : 0;
      return bTotal - aTotal;
    });

    return totalRow ? [...sortedNonTotalRows, totalRow] : sortedNonTotalRows;
  };

  const handleSort = (key) => {
    if (!key) return;

    let direction = "desc";
    if (sortConfig?.key === key && sortConfig?.direction === "desc") {
      direction = "asc";
    } else if (sortConfig?.key === key && sortConfig?.direction === "asc") {
      setSortConfig({ key: null, direction: null });
      return;
    }
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ dataField }) => {
    if (!dataField) return null;

    const isActive = sortConfig?.key === dataField;
    const direction = sortConfig?.direction;

    return (
      <span
        style={{
          marginLeft: "8px",
          cursor: "pointer",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "32px",
          height: "32px",
          verticalAlign: "middle",
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleSort(dataField);
        }}
      >
        <div
          style={{
            width: "0",
            height: "0",
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderBottom: `8px solid ${
              isActive && direction === "asc" ? "#007bff" : "#cbd5e0"
            }`,
            marginBottom: "2px",
            transition: "all 0.2s ease",
          }}
        />
        <div
          style={{
            width: "0",
            height: "0",
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: `8px solid ${
              isActive && direction === "desc" ? "#007bff" : "#cbd5e0"
            }`,
            transition: "all 0.2s ease",
          }}
        />
      </span>
    );
  };

  const sortedTableData = getSortedData();

  const onClickReset = () => {
    setFilters(initialFilters);
    setAllFilters(iniAllFilters);
    setSortConfig({ key: null, direction: null });
  };

  const fetchSalesData = () => {
    if (!subsTypeSalesLoading) {
      dispatch(
        fetchSubsTypeSalesData({
          filters: filters,
          token: token,
        })
      );
    }
  };

  useEffect(() => {
    setAllFilters((prevs) => ({
      ...prevs,
      selectedStartDate: currentDateOfTheMonth,
      selectedEndDate: currentDateOfTheMonth,
    }));
  }, []);

  useEffect(() => {
    setAllFilters((prevs) => ({
      ...prevs,
      selAddedby: prevAllFilters.selAddedby || "",
    }));
  }, [prevAllFilters.selAddedby]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      gym_id: isClusterManager ? "" : gymId || "",
      date_from: allFilters.selectedStartDate
        ? convertStraight(allFilters.selectedStartDate)
        : "",
      date_to: allFilters.selectedEndDate
        ? convertStraight(allFilters.selectedEndDate)
        : "",
      city: allFilters.selCity?.label || "",
      cluster_manager_id: allFilters.selAddedby?.value || "",
    }));
  }, [allFilters, gymId]);

  useInterval(fetchSalesData, 300000);

  useEffect(() => {
    fetchSalesData();
  }, [filters]);

  useEffect(() => {
    if (registerApiCall) {
      registerApiCall(fetchSalesData);
    }
  }, [registerApiCall]);

  useEffect(() => {
    if (subsTypeSales?.status && subsTypeSales?.data?.length > 1) {
      setTbDatas((prevs) => ({
        ...prevs,
        allData: subsTypeSales.data,
      }));
    } else if (subsTypeSales?.status && subsTypeSales?.data?.length === 1) {
      setTbDatas((prevs) => ({
        ...prevs,
        allData: subsTypeSales.data,
      }));
    } else {
      setTbDatas(iniTbDatas);
    }
  }, [subsTypeSales]);

  const tbColumns = [
    {
      dataField: "gym_name",
      text: "Gym Name",
      formatter: (_, row) => {
        return (
          <span
            className="tb-data minWidth150"
            style={{
              fontWeight: row?.gym_name?.toLowerCase() === "total" ? "700" : "",
              fontSize: row?.gym_name?.toLowerCase() === "total" ? "18px" : "",
              textAlign: "start",
            }}
          >
            {row?.gym_name || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "active_members",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Active Members
          <SortIcon dataField="active_members" />
        </span>
      ),
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            {convertThenFormat(row?.active_members || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_checkins",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Checkins
          <SortIcon dataField="total_checkins" />
        </span>
      ),
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            {convertThenFormat(row?.total_checkins || 0)}
          </span>
        );
      },
    },
    {
      dataField: "scheduled",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Scheduled Payment
          <SortIcon dataField="scheduled" />
        </span>
      ),
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹{row?.scheduled ? convertThenFormat(row.scheduled) : 0}
          </span>
        );
      },
    },
    {
      dataField: "auth_payments",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          New Recurring Payment
          <SortIcon dataField="auth_payments" />
        </span>
      ),
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹{row?.auth_payments ? convertThenFormat(row.auth_payments) : 0}
          </span>
        );
      },
    },
    {
      dataField: "renewals",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Renewals(Non Recurring)
          <SortIcon dataField="renewals" />
        </span>
      ),
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹{row?.renewals ? convertThenFormat(row.renewals) : 0}
          </span>
        );
      },
    },
    {
      dataField: "new_subscriptions",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          New One Time Payment
          <SortIcon dataField="new_subscriptions" />
        </span>
      ),
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹
            {row?.new_subscriptions
              ? convertThenFormat(row.new_subscriptions)
              : 0}
          </span>
        );
      },
    },
    {
      dataField: "addon_pt",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Personal Training
          <SortIcon dataField="addon_pt" />
        </span>
      ),
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹{row?.addon_pt ? convertThenFormat(row.addon_pt) : 0}
          </span>
        );
      },
    },
    {
      dataField: "regular_acheived",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          regular acheived
          <SortIcon dataField="regular_acheived" />
        </span>
      ),
      // text: "regular acheived",
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹
            {row?.regular_acheived
              ? convertThenFormat(row.regular_acheived)
              : 0}
          </span>
        );
      },
    },
    {
      dataField: "addon_pt_acheived",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          PT acheived
          <SortIcon dataField="addon_pt_acheived" />
        </span>
      ),
      // text: "PT acheived",
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹
            {row?.addon_pt_acheived
              ? convertThenFormat(row.addon_pt_acheived)
              : 0}
          </span>
        );
      },
    },
    {
      dataField: "total",
      text: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Total Sale
          <SortIcon dataField="total" />
        </span>
      ),
      formatter: (_, row) => {
        const above10 = parseFloat(row?.total) >= 1000000;
        const above5 = parseFloat(row?.total) >= 500000;

        return (
          <span
            className={`tb-data ${
              row?.gym_name?.toLowerCase() === "total"
                ? "withPadding borderRdius8 bold"
                : ""
            } `}
            style={{
              background:
                row?.gym_name?.toLowerCase() === "total" ? "#379C00" : "",
              color: row?.gym_name?.toLowerCase() === "total" ? "#fff" : "",
            }}
          >
            ₹{row?.total ? convertThenFormat(row.total) : 0}
            {row?.gym_id === "uQu9T5e9YoRMC" && above10 ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-arrow-up-short"
                viewBox="0 0 16 16"
                style={{ color: "#379C00", width: "20px", height: "20px" }}
              >
                <path
                  fillRule="evenodd"
                  d="M8 12a.5.5 0 0 0 .5-.5V5.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 5.707V11.5a.5.5 0 0 0 .5.5"
                />
              </svg>
            ) : above5 &&
              row?.gym_name !== "Total" &&
              row?.gym_id !== "uQu9T5e9YoRMC" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-arrow-up-short"
                viewBox="0 0 16 16"
                style={{ color: "#379C00", width: "20px", height: "20px" }}
              >
                <path
                  fillRule="evenodd"
                  d="M8 12a.5.5 0 0 0 .5-.5V5.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 5.707V11.5a.5.5 0 0 0 .5.5"
                />
              </svg>
            ) : null}
          </span>
        );
      },
    },
  ];

  return (
    <div
      className={`mainContainer ${
        isBetaMode ? "home-gymwise-sales-container mt30" : ""
      }`}
    >
      <CmnFilters
        from={"sales-value"}
        userInfoData={userInfoData}
        onClickReset={onClickReset}
        filters={allFilters}
        setFilters={setAllFilters}
        infoTxt={`Active membership is calculated based on the expiry date after or equals to : ${filters.date_to}`}
        isReload={fetchSalesData}
      />

      <div className="fullWidthFlexCenter overflowAuto separatedTable adjustable-table no-border-table mod-height-100vh mt15">
        {subsTypeSalesLoading ? (
          <TableSkeleton
            columns={tbColumns.length}
            rows={tbDatas.allData?.length || 10}
          />
        ) : (
          <PaymentLinkTable
            tableData={sortedTableData}
            tableColumns={tbColumns}
          />
        )}
      </div>
    </div>
  );
};

export default SaleValues;
